import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    Optional,
    ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
    PaypalButtonsInstance,
    PaypalSdkService,
} from 'src/app/core/services/paypal-sdk.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { PaymentsService } from 'src/app/modules/payments/payments.service';

export type PaymentMethodType = 'card' | 'paypal';

export interface PaymentMethodDialogData {
    paypalSubscriptionBody?: Record<string, unknown>;
}

export type PaymentMethodDialogResult =
    | PaymentMethodType
    | {
        method: 'paypal';
        subscriptionId: string;
        baToken?: string;
    };

@Component({
    selector: 'payment-method-popup',
    templateUrl: './payment-method-popup.component.html',
    styleUrls: ['./payment-method-popup.component.scss'],
})
export class PaymentMethodPopupDialog implements AfterViewInit, OnDestroy {
    @ViewChild('paypalButtonContainer') paypalButtonContainer?: ElementRef<HTMLDivElement>;

    paypalLoading = false;
    paypalUnavailable = false;
    paypalRendering = false;

    private paypalButtons: PaypalButtonsInstance | null = null;

    constructor(
        public dialogRef: MatDialogRef<PaymentMethodPopupDialog, PaymentMethodDialogResult>,
        private paypalSdk: PaypalSdkService,
        private paymentsService: PaymentsService,
        private notify: NotificationService,
        private translate: TranslateService,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: PaymentMethodDialogData | null,
    ) {}

    ngAfterViewInit(): void {
        setTimeout(() => this.renderPaypalButton());
    }

    ngOnDestroy(): void {
        this.destroyPaypalButton();
    }

    close(): void {
        this.dialogRef.close();
    }

    selectMethod(method: PaymentMethodType): void {
        this.dialogRef.close(method);
    }

    private async renderPaypalButton(): Promise<void> {
        if (!this.paypalButtonContainer?.nativeElement) {
            return;
        }

        if (!this.paypalSdk.isConfigured) {
            this.paypalUnavailable = true;
            return;
        }

        if (!this.data?.paypalSubscriptionBody) {
            this.paypalUnavailable = true;
            return;
        }

        this.paypalLoading = true;
        this.paypalRendering = true;

        try {
            const paypal = await this.paypalSdk.load();
            this.destroyPaypalButton();

            const container = this.paypalButtonContainer.nativeElement;
            container.innerHTML = '';

            this.paypalButtons = paypal.Buttons({
                style: {
                    layout: 'vertical',
                    color: 'gold',
                    shape: 'rect',
                    label: 'paypal',
                    height: 48,
                    disableMaxWidth: true,
                },
                fundingSource: 'paypal',
                createSubscription: async () => {
                    const response = await this.paymentsService.createPaypalSubscription(
                        this.data.paypalSubscriptionBody,
                    ).toPromise();

                    if (!response?.subscriptionId) {
                        throw new Error('PayPal subscription ID not received');
                    }

                    return response.subscriptionId;
                },
                onApprove: (approveData) => {
                    this.dialogRef.close({
                        method: 'paypal',
                        subscriptionId: approveData.subscriptionID,
                        baToken: approveData.billingToken,
                    });
                },
                onCancel: () => {
                    this.notify.showError(
                        this.translate.instant('PAYPAL_PAYMENT_CANCELLED'),
                    );
                },
                onError: () => {
                    this.notify.showError(
                        this.translate.instant('PAYPAL_PAYMENT_FAILED'),
                    );
                },
            });

            if (!this.paypalButtons.isEligible()) {
                this.paypalUnavailable = true;
                return;
            }

            await this.paypalButtons.render(container);
        } catch {
            this.paypalUnavailable = true;
        } finally {
            this.paypalLoading = false;
            this.paypalRendering = false;
        }
    }

    private destroyPaypalButton(): void {
        if (this.paypalButtons) {
            try {
                this.paypalButtons.close();
            } catch {
                // PayPal SDK may throw if already closed
            }
            this.paypalButtons = null;
        }

        if (this.paypalButtonContainer?.nativeElement) {
            this.paypalButtonContainer.nativeElement.innerHTML = '';
        }
    }
}
